<template>
  <div>
    <RevTable
      v-if="data"
      :caption
      :collection
      :columns
      striped-rows
      transparent-header
    >
      <template v-for="(slot, index) of slotNames" :key="index" #[slot]="scope">
        <slot :name="slot" v-bind="scope" />
      </template>
    </RevTable>
    <div
      v-if="pagination.isShown"
      class="flex flex-col-reverse items-center p-24 lg:flex-row lg:justify-between"
    >
      <Pagination
        class="flex flex-1 lg:mt-0"
        :current-page
        :page-count="pagination.pageCount"
        @click="$emit('page-change', $event)"
      />
      <span class="body-2-bold">
        {{ pagination.count }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends DjangoPaginatedResults<unknown>">
import { computed } from 'vue'

import type { DjangoPaginatedResults } from '@backmarket/http-api/src/api-django'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTable } from '@ds/components/Table'
import type { Column } from '@ds/components/Table/Table.constant'
import type { SlotContent } from '@ds/types/SlotContent'

import translations from './PaginatedTable.translations'

const DEFAULT_PAGE_SIZE = 12

// Maybe we can find better types
type SlotName = `body-${string}`
type Item = T['results'][number]
type Items = Record<string, Item>[]

const props = withDefaults(
  defineProps<{
    columns: Column[]
    data: T
    currentPage: number
    pageSize?: number
    caption?: string
  }>(),
  { pageSize: DEFAULT_PAGE_SIZE, caption: undefined },
)

defineEmits(['page-change'])

const slots = defineSlots<{
  [name: SlotName]: SlotContent<{ column: Column; item: Item }>
}>()

const i18n = useI18n()

const slotNames = Object.keys(slots) as SlotName[]

const collection = computed(() => props.data.results as Items)

const pagination = computed(() => {
  const start = (props.currentPage - 1) * props.pageSize
  const end = start + props.pageSize

  return {
    isShown: props.data.count > props.pageSize,
    pageCount: Math.ceil(props.data.count / props.pageSize),
    count: i18n(translations.paginationCount, {
      start,
      end: Math.min(end, props.data.count),
      count: props.data.count,
    }),
  }
})
</script>
